import { Badge, Box, Divider, Group, Paper, SimpleGrid, Text } from "@mantine/core"
import { CheckIcon, InfoIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { formatQuantity } from "../../utils/format-quantity"
import { ListState } from "../ui"
import { DecisionBadge } from "./atoms"
import { useShortageDecisionQuery, useShortageThresholds } from "./hooks"

function Kpi({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <Box ta="center">
      <Text fz={14} fw={600} c="gray.8">
        {label}
      </Text>
      <Text fz={28} fw={700} lh={1.3}>
        {value}
      </Text>
    </Box>
  )
}

export function DecisionPanel({ pullSheetId }: Readonly<{ pullSheetId: string }>) {
  const { t } = useTranslation("delivery")
  const { data, isLoading, error, mutate } = useShortageDecisionQuery(pullSheetId)
  const thresholds = useShortageThresholds()

  if (isLoading) return <ListState state="loading" columns={2} rows={4} />
  if (error) return <ListState state="error" title={t("shortage.decision.loadError")} onRetry={() => mutate()} />
  if (!data) return null

  const bands = [
    { code: "AUTO_SPLIT", text: t("shortage.decision.below", { n: thresholds.review }) },
    { code: "COORDINATOR_REVIEW", text: t("shortage.decision.between", { a: thresholds.review, b: thresholds.escalation }) },
    { code: "MANAGER_ESCALATION", text: t("shortage.decision.above", { n: thresholds.escalation }) },
  ]

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="center" mb="sm">
        <Text fz={16} fw={700}>
          {t("shortage.decision.title")}
          <HelpIcon helpKey="order.supplier-shipping-list.section.decision" variant="section" />
        </Text>
        <DecisionBadge decision={data.decision} />
      </Group>
      <Divider mb="sm" />

      <SimpleGrid cols={3} spacing="xs">
        <Kpi label={t("shortage.decision.totalLines")} value={data.totalLines} />
        <Kpi label={t("shortage.decision.shortLines")} value={data.shortLines} />
        <Kpi label={t("shortage.decision.percent")} value={`${data.shortagePercent.toFixed(1)}%`} />
      </SimpleGrid>
      <Text fz={14} c="gray.8" ta="center" mt="xs">
        {t("shortage.decision.quantity", { short: formatQuantity(data.shortQuantity), total: formatQuantity(data.totalQuantity) })}
      </Text>

      {data.options?.length > 0 && (
        <Box mt="md">
          <Text fz={14} fw={600} mb="xs">
            {t("shortage.decision.options")}
          </Text>
          <Group gap="xs">
            {data.options.map((opt) => (
              <Badge key={opt} variant="default" size="lg" tt="none" fw={500}>
                {t(`shortage.decision.option.${opt}`, { defaultValue: "-" })}
              </Badge>
            ))}
          </Group>
        </Box>
      )}

      <Paper bg="gray.0" radius="md" p="sm" mt="md">
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <Box c="gray.8" mt={2}>
            <InfoIcon size={18} />
          </Box>
          <Text fz={14}>{t(`shortage.decision.info.${data.decision}`, { defaultValue: "" })}</Text>
        </Group>
      </Paper>

      <Text fz={14} fw={700} mt="md" mb={4}>
        {t("shortage.decision.thresholds")}
      </Text>
      {bands.map((band) => (
        <Group key={band.code} justify="space-between" py={6} style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
          <Text fz={14}>{band.text}</Text>
          <Group gap={6} fw={band.code === data.decision ? 700 : 400}>
            {band.code === data.decision && <CheckIcon size={14} />}
            <Text fz={14} fw="inherit">
              {t(`shortage.decision.${band.code}`)}
            </Text>
          </Group>
        </Group>
      ))}
    </Paper>
  )
}
